import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { PinBoxComponent } from '../pin-box/pin-box.component';

import each from 'lodash/each';
import eachRight from 'lodash/eachRight';
import size from 'lodash/size';

@Component({
  selector: 'hub-pin-input',
  templateUrl: './pin-input.component.html',
  styleUrls: ['./pin-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PinInputComponent implements OnInit {
  /**
   * The maximum number of characters allowed
   * */
  @Input() maxPin: number = 4;

  /**
   * The hidden form input that captures the user keydown event
   */
  @ViewChild('pinFormInput', { static: true }) pinFormInput: ElementRef;

  /**
   * The set of pin box components
   */
  @ViewChildren(PinBoxComponent) pinBoxes: QueryList<PinBoxComponent>;

  /**
   * Event emitter to indicate the component pin has changed
   */
  @Output() changed: EventEmitter<string> = new EventEmitter();

  /**
   * Event emitter to indicate the user clicked enter key
   */
  @Output() enterKey: EventEmitter<string> = new EventEmitter();

  /**
   * The array value of pin box components
   */
  public pinBoxesElement: PinBoxComponent[] = [];

  /**
   * A dummy array used to render the number of pin boxes
   */
  public pinBoxesElementDummy: number[] = [];

  private _lock: boolean = false;

  constructor(public changeRef: ChangeDetectorRef) {}

  ngOnInit() {
    // this is just a fallback - ngOnChanges should do the setting
    if (size(this.pinBoxesElement) == 0 && this.pinBoxes) {
      this.pinBoxesElement = this.pinBoxes.toArray();
    }
  }

  ngOnChanges(changes) {
    // listen to any max pin changes and update the UI
    if (changes['maxPin']) {
      this.pinBoxesElementDummy = Array(this.maxPin).map((x, i) => i);
      this.changeRef.markForCheck();

      // make sure you grab the changes
      this.changeRef.detectChanges();
      if (this.pinBoxes) {
        this.pinBoxesElement = this.pinBoxes.toArray();
        this.pinHasChanged();
      }
    }
  }

  /**
   * Mark the empty pin boxes as invalid
   */
  markEmptyAsInvalid(): void {
    if (this._lock) return;

    if (this.pinBoxesElement) {
      each(this.pinBoxesElement, (elem: PinBoxComponent) => {
        if (size(elem.char) == 0) {
          elem.updateChar('', false, true);
        }
      });
    }
  }

  /**
   * Clear the pin boxes characters
   * @param markAsInvalid Flagger to set the pin boxes as invalid. Default is FALSE.
   */
  clearPinBox(markAsInvalid: boolean = false): void {
    if (this._lock) return;

    if (this.pinBoxesElement) {
      each(this.pinBoxesElement, (elem: PinBoxComponent) => {
        elem.updateChar('', false, markAsInvalid);
      });
    }
  }

  /**
   * Keydown handler that blocks non-numeric keys
   * @param $event The keyboard keydown event
   */
  blockKeys($event: KeyboardEvent) {
    if (this._lock) {
      $event.preventDefault();
      $event.stopImmediatePropagation();
      return;
    }

    // backspace
    if ($event.code == 'Backspace' || $event.key == 'Backspace') {
      this.removePin();
      return;
    }

    // enter
    if ($event.code == 'Enter' || $event.key == 'Enter') {
      this.enterKey.emit();
      return;
    }

    if ($event.ctrlKey || $event.altKey || $event.metaKey) {
      $event.preventDefault();
      $event.stopImmediatePropagation();
    } else {
      const eventKey: number = +$event.key;
      if (!isNaN(eventKey)) {
        this.addPin($event.key.toLowerCase());
      } else {
        $event.preventDefault();
        $event.stopImmediatePropagation();
      }
    }
  }

  /**
   * Removes a character from the pin boxes. Search starts from the right.
   */
  removePin(): void {
    if (this._lock) return;

    eachRight(this.pinBoxesElement, (elem: PinBoxComponent) => {
      if (size(elem.char) > 0) {
        elem.updateChar('', false);
        this.pinHasChanged();
        return false;
      }
    });
  }

  /**
   * Adds a pin character to an empty pin box
   * @param pin The pin character to set
   */
  addPin(pin: string): void {
    if (this._lock) return;

    each(this.pinBoxesElement, (elem: PinBoxComponent) => {
      if (size(elem.char) == 0) {
        elem.updateChar(pin, true);
        this.pinHasChanged();
        return false;
      } else {
        // force it to asterisk
        elem.updateChar(elem.char, false, false);
      }
    });
  }

  /**
   * Fire event to indicate the component pin value has changed
   */
  pinHasChanged(): void {
    this.changed.emit(this.getValue());
  }

  /**
   * Focus the input component. This will show the device keyboard.
   */
  focusInput() {
    if (this.pinFormInput) {
      this.pinFormInput.nativeElement.focus();
    }
  }

  /** Get the current pin */
  getValue(): string {
    let pin: string = '';
    each(this.pinBoxesElement, (elem: PinBoxComponent) => {
      pin += elem.char;
    });

    return pin;
  }

  /**
   * Locks the component and prevents any further value change
   */
  lock(): void {
    this._lock = true;
  }

  /**
   * Unlocks the component to allow value change
   */
  unlock(): void {
    this._lock = false;
  }
}
